import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import axios, { AxiosHeaders } from 'axios';
import { BASE_URL } from '../constants';
import { getSecurityToken } from '../utils/security';

type CustomAxiosRequestConfig<T> = Omit<AxiosRequestConfig, 'params'> & {
  params: T;
};

type AxiosInstanceType<
  GetParams = unknown,
  GetResponse = unknown,
  PostParams = unknown,
  PostData = unknown,
  PostResponse = unknown,
  PutParams = unknown,
  PutData = unknown,
  PutResponse = unknown,
  PatchParams = unknown,
  PatchData = unknown,
  PatchResponse = unknown,
  DeleteParams = unknown,
  DeleteResponse = unknown
> = {
  get(config?: CustomAxiosRequestConfig<GetParams>): Promise<GetResponse>;
  post(
    data?: PostData,
    config?: CustomAxiosRequestConfig<PostParams>
  ): Promise<PostResponse>;
  put(
    data?: PutData,
    config?: CustomAxiosRequestConfig<PutParams>
  ): Promise<PutResponse>;
  patch(
    data?: PatchData,
    config?: CustomAxiosRequestConfig<PatchParams>
  ): Promise<PatchResponse>;
  delete(
    config?: CustomAxiosRequestConfig<DeleteParams>
  ): Promise<DeleteResponse>;
} & AxiosInstance;

export const createTypedAxiosInstance = <
  GetParams = unknown,
  GetResponse = unknown,
  PostParams = unknown,
  PostData = unknown,
  PostResponse = unknown,
  PutParams = unknown,
  PutData = unknown,
  PutResponse = unknown,
  PatchParams = unknown,
  PatchData = unknown,
  PatchResponse = unknown,
  DeleteParams = unknown,
  DeleteResponse = unknown
>(
  apiUrl: string
): AxiosInstanceType<
  GetParams,
  GetResponse,
  PostParams,
  PostData,
  PostResponse,
  PutParams,
  PutData,
  PutResponse,
  PatchParams,
  PatchData,
  PatchResponse,
  DeleteParams,
  DeleteResponse
> => {
  const instance = axios.create({
    baseURL: BASE_URL,
    headers: {
      Authorization: getSecurityToken() ? `Bearer ${getSecurityToken()}` : null,
    },
  });

  // Read current token for each request to avoid stale headers after runtime login.
  instance.interceptors.request.use((config) => {
    const token = getSecurityToken();

    if (!config.headers) {
      config.headers = new AxiosHeaders();
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      delete config.headers.Authorization;
    }

    return config;
  });

  return {
    ...instance,
    get: (config?: CustomAxiosRequestConfig<GetParams>) =>
      instance.get(apiUrl, config),
    post: (data?: PostData, config?: CustomAxiosRequestConfig<PostParams>) =>
      instance.post(apiUrl, data, config),
    put: (data?: PutData, config?: CustomAxiosRequestConfig<PutParams>) =>
      instance.put(apiUrl, data, config),
    patch: (data?: PatchData, config?: CustomAxiosRequestConfig<PatchParams>) =>
      instance.patch(apiUrl, data, config),
    delete: (config?: CustomAxiosRequestConfig<DeleteParams>) =>
      instance.delete(apiUrl, config),
  } as AxiosInstanceType<
    GetParams,
    GetResponse,
    PostParams,
    PostData,
    PostResponse,
    PutParams,
    PutData,
    PutResponse,
    PatchParams,
    PatchData,
    PatchResponse,
    DeleteParams,
    DeleteResponse
  >;
};
